import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import TextField from "components/common/TextField";
import { Plus, X } from "lucide-react";
import { DelegationTask } from "protogen/delegations_pb";
import { ApplyDelegationTaskDetailsUpdateRequest } from "protogen/delegation_service_pb";
import { useApplyDelegationTaskDetailsUpdate } from "services/delegation";
import { useListMemberSecrets, useListSecrets } from "services/secrets";
import {
  ListMemberSecretsRequest,
  ListSecretsRequest,
} from "protogen/secrets_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdvisor } from "common/userUtils";
import Select from "components/common/Select";
import FlexBox from "components/common/FlexBox";
import {
  DelegationDetail,
  parseDetails,
  separateDetailsByType,
} from "components/delegation/DelegationDetails";

type Props = {
  open: boolean;
  task: DelegationTask;
  familyRef?: string;
  onClose: () => void;
  onSaved: () => void;
};

type SecretRow = {
  id: string;
  selectedSecretRef: string | null;
};

export default function EditDelegationDetailsDialog({
  open,
  task,
  familyRef,
  onClose,
  onSaved,
}: Props) {
  const [description, setDescription] = useState("");
  const [details, setDetails] = useState<DelegationDetail[]>([]);
  const [secretRows, setSecretRows] = useState<SecretRow[]>([]);
  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = isUserAdvisor(currentUser);
  const { request: listAdvisorSecrets, data: advisorSecretsData } =
    useListSecrets();
  const { request: listMemberSecrets, data: memberSecretsData } =
    useListMemberSecrets();
  const familySecrets = isAdvisor
    ? advisorSecretsData?.secrets
    : memberSecretsData?.secrets;
  const { request: applyDetails, loading } =
    useApplyDelegationTaskDetailsUpdate(() => {
      onSaved();
      onClose();
    });

  useEffect(() => {
    if (!open) return;
    const parsed = parseDetails(task.detailsJson);
    const { normal, secret } = separateDetailsByType(parsed);
    setDescription(task.description || "");
    setDetails(normal);
    setSecretRows(
      secret.map((s) => ({
        id: Math.random().toString(),
        selectedSecretRef: s.value || null,
      })),
    );
    if (isAdvisor) {
      if (familyRef) {
        const req = new ListSecretsRequest();
        req.familyRef = familyRef;
        listAdvisorSecrets(req);
      }
    } else {
      listMemberSecrets(new ListMemberSecretsRequest());
    }
  }, [open, task, familyRef, isAdvisor]);

  const addSecretRow = () => {
    setSecretRows((prev) => [
      ...prev,
      { id: Math.random().toString(), selectedSecretRef: null },
    ]);
  };

  const updateSecretRow = (rowId: string, secretRef: string | null) => {
    setSecretRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, selectedSecretRef: secretRef } : row,
      ),
    );
  };

  const removeSecretRow = (rowId: string) => {
    setSecretRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleSave = async () => {
    const secretDetails = secretRows
      .filter((row) => row.selectedSecretRef)
      .map((row) => {
        const ref = row.selectedSecretRef!;
        const secret = familySecrets?.find((s) => s.ref === ref);
        return {
          name: secret?.name || "Secret",
          value: ref,
          detailType: "grant",
          entityType: "secret",
        };
      });
    const req = new ApplyDelegationTaskDetailsUpdateRequest();
    req.ref = task.ref;
    req.description = description;
    req.detailsJson = JSON.stringify([...details, ...secretDetails]);
    await applyDetails(req);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit details</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {task.assignee && (
            <Alert severity="info">
              Assigned to {task.assignee.firstName} {task.assignee.lastName}.
              Saving updates what they can see, including secret access.
            </Alert>
          )}
          <TextField
            label="Instructions"
            value={description}
            multiline
            minRows={3}
            fullWidth
            onChange={(e) => setDescription(e.target.value)}
          />
          {details.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ marginBottom: "8px" }}>
                Details
              </Typography>
              {details.map((detail, index) => (
                <Box
                  key={`${detail.name}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Typography variant="bodyHeavy">{detail.name}</Typography>
                  <TextField
                    value={detail.value}
                    size="small"
                    fullWidth
                    onChange={(e) =>
                      setDetails((prev) =>
                        prev.map((d, i) =>
                          i === index ? { ...d, value: e.target.value } : d,
                        ),
                      )
                    }
                  />
                </Box>
              ))}
            </Box>
          )}
          <Box>
            <FlexBox alignItems="center" justifyContent="space-between">
              <Typography variant="h5">Secrets to share</Typography>
              <Button
                variant="text"
                startIcon={<Plus size={16} />}
                onClick={addSecretRow}
                sx={{ height: "unset" }}
              >
                Add
              </Button>
            </FlexBox>
            {!familySecrets ? (
              <Typography variant="bodySmall" color="text.secondary">
                Loading secrets...
              </Typography>
            ) : familySecrets.length === 0 ? (
              <Typography variant="bodySmall" color="text.secondary">
                No secrets in the vault.
              </Typography>
            ) : (
              <Box sx={{ marginTop: "8px" }}>
                {secretRows.length === 0 && (
                  <Typography variant="bodySmall" color="text.secondary">
                    No secrets included.
                  </Typography>
                )}
                {secretRows.map((row) => (
                  <FlexBox
                    key={row.id}
                    alignItems="center"
                    gap="8px"
                    sx={{ marginBottom: "8px" }}
                  >
                    <Select
                      placeholder="Select a secret"
                      value={row.selectedSecretRef ?? ""}
                      onChange={(e) => {
                        const value = e.target.value as string;
                        updateSecretRow(row.id, value || null);
                      }}
                      options={[
                        { label: "Select a secret", value: "" },
                        ...familySecrets.map((secret) => ({
                          label: secret.name,
                          value: secret.ref,
                        })),
                      ]}
                      variant="detachedOutlined"
                      size="small"
                      sx={{ flex: 1, minWidth: 200 }}
                    />
                    <X
                      size={18}
                      color="#6B6E7B"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeSecretRow(row.id)}
                    />
                  </FlexBox>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
